/**
 * Skeleton primitives for list pages. Shape-first, not colour-first: matches
 * the underlying card / row layout so the transition to real content is
 * imperceptible.
 *
 * Usage:
 *   <SkeletonCardGrid count={9} />
 *   <SkeletonRows count={5} />
 */

function Shimmer({ className = "" }) {
  return <div className={`bg-zinc-100 animate-pulse ${className}`} />;
}

export function SkeletonCard({ testid }) {
  return (
    <div data-testid={testid} className="card overflow-hidden">
      <Shimmer className="aspect-[16/9] w-full" />
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Shimmer className="h-3 w-12" />
          <Shimmer className="h-4 w-16" />
        </div>
        <Shimmer className="h-5 w-3/4" />
        <Shimmer className="h-3 w-full" />
        <Shimmer className="h-3 w-5/6" />
        <div className="pt-4 border-t border-zinc-100 flex gap-4">
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-3 w-32" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonCardGrid({ count = 6, testid = "skeleton-grid" }) {
  return (
    <div
      data-testid={testid}
      className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      aria-busy="true"
      aria-label="Loading"
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} testid={`${testid}-item-${i}`} />
      ))}
    </div>
  );
}

export function SkeletonRow({ testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-4 border-b border-zinc-100 py-4"
    >
      <Shimmer className="w-14 h-14 flex-shrink-0 rounded-full" />
      <div className="flex-1 space-y-2 min-w-0">
        <Shimmer className="h-4 w-1/2" />
        <Shimmer className="h-3 w-3/4" />
      </div>
      <Shimmer className="h-3 w-16 flex-shrink-0" />
    </div>
  );
}

export function SkeletonRows({ count = 5, testid = "skeleton-rows" }) {
  return (
    <div data-testid={testid} aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i} testid={`${testid}-item-${i}`} />
      ))}
    </div>
  );
}

export function SkeletonText({ lines = 3, testid = "skeleton-text" }) {
  return (
    <div data-testid={testid} className="space-y-2" aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer key={i} className={`h-3 ${i === lines - 1 ? "w-2/3" : "w-full"}`} />
      ))}
    </div>
  );
}
